import { z } from "zod";

import { hexColorSchema } from "./catalog.js";
import { designDocumentSchema } from "./designs.js";
import { millimeterSchema, moneyFenSchema, uuidSchema } from "./primitives.js";

/**
 * 作品与设计师。
 *
 * **作品 = 一串配好的方案**：一份材料清单（哪些规格各几颗）+ 主图与说明。
 * 用户看到喜欢的作品可以「套用」，小程序据此生成一份设计再进 DIY 页继续改。
 *
 * 作品**不存价格**：价格由服务端按当前目录现算（材料小计）。这样后台改价之后，
 * 作品上的价格自然跟着变，不会出现「作品标的价 vs 实际下单价」两套数字。
 */

/** 作品归属：设计师款与优秀客订分开展示（参考图里是两个标签页）。 */
export const workKindSchema = z.enum(["designer", "customer"]);

export type WorkKind = z.infer<typeof workKindSchema>;

/** 设计师。首版只有展示用的资料，入驻与佣金结算还没做。 */
export const designerSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(32),
  /** 头像地址（相对路径）。没有头像时为 null，界面用占位。 */
  avatarPath: z.string().min(1).nullable(),
  bio: z.string().max(120),
  /** 评分，按十分之一分存（49 表示 4.9），没有评分为 null。不做假评分。 */
  ratingTenths: z.int().min(0).max(50).nullable(),
  /** 已发布作品数，由服务端统计，不由后台手填。 */
  workCount: z.int().nonnegative(),
});

export type DesignerPayload = z.infer<typeof designerSchema>;

/** 小程序读取的设计师列表：只需要展示资料，没有分页。 */
export const publicDesignerListSchema = z.array(designerSchema);

export type PublicDesignerListPayload = z.infer<typeof publicDesignerListSchema>;

/** 作品列表里的一项：够卡片用，不含材料清单。 */
export const workSummarySchema = z.object({
  id: uuidSchema,
  title: z.string().min(1).max(40),
  kind: workKindSchema,
  /** 材料小计，整数分。按当前目录现算。 */
  priceFen: moneyFenSchema.nonnegative(),
  coverPath: z.string().min(1).nullable(),
  /** 被套用的次数。 */
  applyCount: z.int().nonnegative(),
  designerName: z.string().min(1).max(32),
  designerAvatarPath: z.string().min(1).nullable(),
  /** 有材料已经下架时为 true，列表上给一个「部分材料下架」的提示。 */
  hasUnavailableMaterial: z.boolean(),
});

export type WorkSummaryPayload = z.infer<typeof workSummarySchema>;

export const workListResponseSchema = z.object({
  items: z.array(workSummarySchema),
  total: z.int().nonnegative(),
});

export type WorkListResponsePayload = z.infer<typeof workListResponseSchema>;

/**
 * 作品列表的筛选与分页。来自 URL 查询串，因此用 coerce 转成目标类型。
 * 上限写进契约，后台与服务端对「一页最多几条」的理解才一致。
 */
export const workListQuerySchema = z.object({
  kind: workKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type WorkListQueryPayload = z.infer<typeof workListQuerySchema>;

/** 材料清单的一行：作品详情与结算都用它。 */
export const workMaterialLineSchema = z.object({
  skuId: uuidSchema,
  /** 石种短名。没有服务端素材时界面按它回退到代码包内的本地照片。 */
  code: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  /** 石种照片地址（相对路径），与托盘用的是同一张。没有素材时为 null。 */
  photoPath: z.string().min(1).nullable(),
  diameterMm: millimeterSchema.positive(),
  stringPathMm: millimeterSchema.positive(),
  color: hexColorSchema,
  /** 单价，整数分。 */
  unitPriceFen: moneyFenSchema.nonnegative(),
  quantity: z.int().positive(),
  /** 小计，整数分。由服务端算，客户端不再乘一遍。 */
  subtotalFen: moneyFenSchema.nonnegative(),
  /** 这一档当前是否可售（上架且有库存）。下架的材料不计入价格。 */
  available: z.boolean(),
});

export type WorkMaterialLinePayload = z.infer<typeof workMaterialLineSchema>;

export const workDetailSchema = workSummarySchema.extend({
  description: z.string().max(600),
  /** 实拍图，横向列表。 */
  photoPaths: z.array(z.string().min(1)),
  materials: z.array(workMaterialLineSchema),
  /** 材料一共几颗。 */
  beadCount: z.int().nonnegative(),
  /** 已经下架、不计入价格的材料行数。为 0 时界面不提示。 */
  unavailableCount: z.int().nonnegative(),
});

export type WorkDetailPayload = z.infer<typeof workDetailSchema>;

/** 套用作品：服务端把作品的材料变成一份可编辑的设计。 */
export const applyWorkResponseSchema = z.object({
  /** 生成的设计。结构与设计文档完全一致，客户端直接载入编辑器继续改。 */
  design: designDocumentSchema,
  /** 套用的作品编号，保存设计时带上它，用于记录来源。 */
  sourceWorkId: uuidSchema,
  /** 有材料已下架时为 true：设计能载入，但那几颗在目录里已经不在了。 */
  hasUnavailableMaterial: z.boolean(),
});

export type ApplyWorkResponsePayload = z.infer<typeof applyWorkResponseSchema>;
